<script setup lang="ts">
/**
 * Bar on top of a Facebook / Instagram history chat (api/app/fb_history.py): says what this read-only chat is, where
 * the pull stopped ("ประวัติก่อนหน้านี้ดึงไม่ได้" instead of a silent gap) and leads back to the live chat.
 */
import { computed, ref, watch } from 'vue'
import { useRouter } from 'vue-router'
import { fbHistoryApi, type FbHistoryStatus } from '../../api/fbhistory'
import type { ConversationDetail } from '../../api/types'
import { doneNote, platformName, refusedLine } from '../../lib/fbHistory'
import Icon from '../ui/Icon.vue'

const props = defineProps<{ detail: ConversationDetail }>()
const router = useRouter()
const status = ref<FbHistoryStatus | null>(null)

watch(
  () => props.detail.contact.id,
  async (id) => {
    status.value = null
    if (!id) return
    try {
      status.value = await fbHistoryApi().status(id)
    } catch {
      status.value = null // the bar still says what this chat is
    }
  },
  { immediate: true },
)

const name = computed(() => (status.value?.supported ? platformName(status.value) : props.detail.channel === 'ig' ? 'Instagram' : 'Facebook'))
const refused = computed(() => (status.value ? refusedLine(status.value) : null))
const note = computed(() => (status.value ? doneNote(status.value) : null))
const liveId = computed(() => status.value?.live_conversation_id ?? null)

function back(): void {
  if (liveId.value) void router.push({ name: 'conversation', params: { id: liveId.value } })
}
</script>

<template>
  <div class="fhb" role="note" :aria-label="`แชทเก่าจาก ${name}`">
    <div class="fhb-r">
      <Icon name="history" class="fhb-ic" />
      <div class="fhb-b">
        <b>แชทเก่าจาก {{ name }} · อ่านอย่างเดียว</b>
        <span>ข้อความก่อนเริ่มใช้ระบบนี้ ดึงจาก {{ name === 'Instagram' ? 'Instagram' : 'Messenger' }} ของเพจ<template v-if="note"> · {{ note }}</template></span>
      </div>
      <button v-if="liveId" type="button" class="fhb-back" @click="back"><Icon name="chevronLeft" /><span>กลับไปแชทปัจจุบัน</span></button>
    </div>
    <p v-if="refused" class="fhb-w"><Icon name="alert" /><span>{{ refused }}</span></p>
  </div>
</template>

<style scoped>
.fhb {
  flex: none;
  display: flex;
  flex-direction: column;
  gap: 6px;
  padding: 10px 20px;
  border-bottom: 1px solid var(--line);
  background: #f7faf8;
}
.fhb-r {
  display: flex;
  align-items: center;
  gap: 10px;
}
.fhb-ic {
  flex: none;
  width: 18px;
  height: 18px;
  color: var(--ac2);
}
.fhb-b {
  flex: 1;
  min-width: 0;
  display: flex;
  flex-direction: column;
  font-size: 12.5px;
  line-height: 19px;
  color: var(--mut);
}
.fhb-b b {
  font-size: 13.5px;
  line-height: 21px;
  font-weight: 600;
  color: var(--ink);
}
.fhb-back {
  flex: none;
  height: 32px;
  padding: 0 12px 0 8px;
  border-radius: 9px;
  border: 1px solid #bfdccd;
  background: #fff;
  display: inline-flex;
  align-items: center;
  gap: 4px;
  font-size: 13px;
  line-height: 20px;
  font-weight: 600;
  color: var(--ac2);
  white-space: nowrap;
}
.fhb-back:hover {
  background: var(--acs);
}
.fhb-back :deep(.ic) {
  width: 15px;
  height: 15px;
}
.fhb-w {
  margin: 0 0 0 28px;
  display: flex;
  align-items: flex-start;
  gap: 6px;
  font-size: 12.5px;
  line-height: 19px;
  color: #7a4700;
}
.fhb-w :deep(.ic) {
  flex: none;
  width: 14px;
  height: 14px;
  margin-top: 2px;
  color: #b86e00;
}
@container (max-width: 520px) {
  .fhb {
    padding: 10px 14px;
  }
  .fhb-r {
    flex-wrap: wrap;
  }
  .fhb-back {
    margin-left: 28px;
  }
  .fhb-w {
    margin-left: 0;
  }
}
</style>
